import React, { useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DrawerFooter } from "@/components/ui/drawer";
import FormNew from "@/components/controls/Form/FormNew";
import { AddFieldProps } from "./types";
import { ADD_LINKED_FIELD_TRANSLATION_KEY } from "./const";
import { useLinkedFieldForm } from "./hooks/useLinkedFieldForm";
import { createFormSections } from "./utils/formSections";
import {
  formatFieldLabel,
  generateLinkedFieldLabel
} from "./utils/linkedFieldUtils";

export const AddLinkedField: React.FC<AddFieldProps> = ({
  onBack,
  onCancel,
  onSave,
  entityType
}) => {
  const { t } = useTranslation();

  const {
    formRef,
    isFormValid,
    formSetValue,
    setFormSetValue,
    isSubmitting,
    selectedObjectType,
    selectedFieldId,
    availableFields,
    fieldOptions,
    existingFieldLabels,
    isLoading,
    handleFormValid,
    handleFormSubmit,
    handleSubmitButton
  } = useLinkedFieldForm({ onSave, entityType });

  const handleObjectTypeSelection = useCallback(
    (value: string | number) => {
      if (typeof formSetValue !== "function") return;
      formSetValue("objectType", value, { shouldValidate: true });
      formSetValue("fieldId", "", { shouldValidate: true });
      formSetValue("fieldLabel", "", { shouldValidate: true });
    },
    [formSetValue]
  );

  const handleFieldIdSelection = useCallback(
    (value: string | number) => {
      if (typeof formSetValue === "function") {
        formSetValue("fieldId", value, { shouldValidate: true });
      }
    },
    [formSetValue]
  );

  useEffect(() => {
    if (typeof formSetValue !== "function") return;
    if (!selectedFieldId || !selectedObjectType) return;
    const field = availableFields.find(
      (f) => String(f.id) === String(selectedFieldId)
    );
    if (field) {
      formSetValue(
        "fieldLabel",
        generateLinkedFieldLabel(selectedObjectType, field),
        { shouldValidate: true }
      );
    }
  }, [formSetValue, selectedFieldId, selectedObjectType, availableFields]);

  const formSections = useMemo(() => {
    return createFormSections({
      selectedObjectType,
      selectedFieldId,
      fieldOptions,
      existingFieldLabels,
      isLoading,
      formSetValue,
      formatFieldLabel: (value: string | number) => {
        const field = availableFields.find(
          (f) => String(f.id) === String(value)
        );
        return field ? formatFieldLabel(field) : "";
      },
      t,
      entityType,
      onObjectTypeSelection: handleObjectTypeSelection,
      onFieldIdSelection: handleFieldIdSelection
    });
  }, [
    selectedObjectType,
    selectedFieldId,
    fieldOptions,
    existingFieldLabels,
    isLoading,
    availableFields,
    entityType,
    formSetValue,
    t,
    handleObjectTypeSelection,
    handleFieldIdSelection
  ]);

  return (
    <>
      <div className="p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]">
        <FormNew
          sections={formSections}
          isFormValid={handleFormValid}
          onSubmit={handleFormSubmit}
          formRef={formRef}
          onSetValue={setFormSetValue}
        />
      </div>

      <DrawerFooter>
        <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
          <Button variant="tertiary" onClick={onBack} className="h-9">
            {t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.back`)}
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="secondaryNoBg" onClick={onCancel} className="h-9">
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSubmitButton}
              disabled={!isFormValid || isSubmitting}
              className="h-9"
            >
              {isSubmitting
                ? t("loading")
                : t(`${ADD_LINKED_FIELD_TRANSLATION_KEY}.linkField`)}
            </Button>
          </div>
        </div>
      </DrawerFooter>
    </>
  );
};
